import React, { useState } from "react";
import styled, { css } from "styled-components";
import * as _ from "lodash";
import { v4 as uuid } from "uuid";
import { useMappedState } from "redux-react-hook";

import {
  BasicModal,
  BasicModalProps
} from "src/library/components/Modal";
import { Button } from "src/library/components/Button";
import { Icons } from "src/library/components/Icon/types";
import {
  IFormOption,
  Select
} from "src/library/components/Forms";
import { IStore } from "src/core/shared/store";

import {
  WidgetDataTypes,
  IWidgetType,
  useDashboardApi,
  WidgetDisplayTypes
} from "../../api/dashboard.api";
import { IconSelect } from "./IconSelect";
import { useDashboardContext } from "../../contexts/DashboardContext";
import { WidgetSlug } from "../../api/widget-data.api";

type CreateWidgetModalProps = Pick<BasicModalProps, "onDismiss" | "open">;

export const CreateWidgetModal: React.ReactFCWithChildren<CreateWidgetModalProps> = ({ onDismiss, open }) => {
  const { currentDashboard, addNewWidget } = useDashboardContext();
  const [ dataType, setDataType ] = useState<WidgetDataTypes>();
  const [ widgetType, setWidgetType ] = useState<IWidgetType>();
  const [ getWidgetTypesRes, getWidgetTypesReq ] = useDashboardApi("GET_WIDGET_TYPES");
  const [ widgetTypeOptions, setWidgetTypeOptions ] = useState<IFormOption[]>();
  const [ displayType, setDisplayType ] = useState<WidgetDisplayTypes>();
  const { allowPendingCustomers } = useMappedState(({ global }: IStore) => ({ allowPendingCustomers: global.settings?.allowPendingCustomers }));

  // get all widget types on mount
  React.useEffect(() => {
    if (!getWidgetTypesRes.data) {
      getWidgetTypesReq(null);
    }
  }, [ getWidgetTypesReq, getWidgetTypesRes.data ]);

  // set widget types options when the data type is selected
  React.useEffect(() => {
    if (getWidgetTypesRes.data && dataType) {
      setWidgetTypeOptions(_.reduce(getWidgetTypesRes.data, (prevOptions: IFormOption[], widgetType) => {
        if (widgetType.dataType === dataType) {
          if (widgetType.slug === WidgetSlug.PendingCustomer && !allowPendingCustomers) {
            // omit pending customers widget if it is not allowed
            return prevOptions;
          }

          return prevOptions.concat({
            value: widgetType.id.toString(),
            text: widgetType.name
          });
        }

        return prevOptions;
      }, []));
    }
  }, [
    allowPendingCustomers,
    dataType,
    getWidgetTypesRes.data
  ]);

  // clear states on dismiss
  const clearStates = React.useCallback(() => {
    setDataType(undefined);
    setWidgetType(undefined);
    setDisplayType(undefined);
  }, []);

  const handleDismiss = React.useCallback(() => {
    clearStates();
    onDismiss();
  }, [ onDismiss, clearStates ]);

  const createWidget = React.useCallback(() => {
    if (currentDashboard && widgetType && displayType) {
      // generate unique ID to give to identify widget on the front end
      // before it is assigned an ID in the database
      const widgetId = uuid();

      addNewWidget({
        id: widgetId,
        dashboardId: currentDashboard.id,
        widgetTypeId: widgetType.id,
        widgetType,
        options: { view: displayType },
        uuid: widgetId
      });
      handleDismiss();
    }
  }, [
    currentDashboard,
    widgetType,
    addNewWidget,
    handleDismiss,
    displayType
  ]);

  const generateDataSelectIcons = React.useCallback((dataType: WidgetDataTypes) => {
    switch (dataType) {
      case WidgetDataTypes.Orders:
        return Icons.CART;
      case WidgetDataTypes.Credits:
        return Icons.CREDIT;
      case WidgetDataTypes.Support:
        return Icons.SUPPORT;
      default:
        return Icons.ADD;
    }
  }, []);

  const generateDisplaySelectIcons = React.useCallback((displayType: WidgetDisplayTypes) => {
    switch (displayType) {
      case WidgetDisplayTypes.Number:
        return Icons.NUMBERS;
      case WidgetDisplayTypes.BarGraph:
        return Icons.BARCHART;
      case WidgetDisplayTypes.LineGraph:
        return Icons.LINEGRAPH;
      case WidgetDisplayTypes.Table:
        return Icons.TABLE;
      default:
        return Icons.ADD;
    }
  }, []);

  return (
    <StyledBasicModal
      title="Create New Widget"
      open={open}
      onDismiss={() => handleDismiss()}
    >
      <div className="modal-content">
        <div className="icon-select-wrapper">
          {getWidgetTypesRes.data && _.uniqBy(getWidgetTypesRes.data, "dataType").map(widgetType => {
            return (
              <IconSelect
                key={widgetType.dataType}
                selected={dataType === widgetType.dataType}
                icon={generateDataSelectIcons(widgetType.dataType)}
                onClick={() => {
                  clearStates();
                  setDataType(widgetType.dataType);
                }}
                label={widgetType.dataType}
              />
            );
          })}
        </div>

        {dataType && widgetTypeOptions?.length && (
          <div className="widget-type-select-wrapper">
            <Select
              label="Widget Type"
              name="widgetType"
              as="select"
              value={widgetType?.id.toString() || ""}
              unselectedOption={{
                text: "Please select",
                value: ""
              }}
              handleChange={e => {
                const selectedWidgetType = _.find(getWidgetTypesRes.data, widgetType => widgetType.id.toString() === e.currentTarget.value);

                setWidgetType(selectedWidgetType);

                // if only one display type is availabel for the seleceted widget then automatically set the state here
                if (selectedWidgetType?.options?.view.length === 1) {
                  setDisplayType(selectedWidgetType?.options?.view[ 0 ]);
                }
              }}
              options={widgetTypeOptions}
            />
          </div>
        )}

        <div className="icon-select-wrapper">
          {widgetType && _.map(widgetType.options.view, displayTypeFromResponse => (
            <IconSelect
              selected={displayType === displayTypeFromResponse}
              key={displayTypeFromResponse}
              icon={generateDisplaySelectIcons(displayTypeFromResponse)}
              onClick={() => setDisplayType(displayTypeFromResponse)}
              label={displayTypeFromResponse.replace("-", " ")}
            />
          ))}
        </div>

        {displayType && (
          <div className="button-wrapper">
            <Button onClick={createWidget}>
              Create Widget
            </Button>
          </div>
        )}
      </div>
    </StyledBasicModal>
  );
};

const StyledBasicModal = styled(BasicModal)`
  ${({ theme }) => css`
    .modal-content{
      padding: ${theme.spacing.padding.md};
    }

    .icon-select-wrapper {
      display: flex;
      flex-direction: row;
      justify-content: space-around;
      padding-bottom: ${theme.spacing.padding.lg};
    }

    .widget-type-select-wrapper {
      display: flex;
      flex-direction: column;
      align-items: centre;
      padding-bottom: ${theme.spacing.padding.lg};

      div {
        max-width: none;
      }
    }

    .button-wrapper{
      display: flex;
      justify-content: flex-end;
    }
  `}
`;
